import React, { useCallback, useEffect } from "react";
import styles from "./Users.module.scss";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import Table from "@/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "@/store/slices/userSlice";
import { setModalOpen } from "@/store/slices/globalSlice";
import { MODAL_ID } from "@/lib/constants";
import AddUser from "./components/AddUser/AddUser";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import UserService from "@/services/api/user";
import type { User } from "@/services/api/user";
import toast from "react-hot-toast";
import { useSearchPagination } from "@/hooks/useSearchPagination";
import ActionsCell from "@/components/ActionCells/ActionsCell";
import Tooltip from "@/components/Tooltip/Tooltip";
import { checkPermission } from "@/lib/utils";
import type { AppDispatch } from "@/store/store";
import type { RootState } from "@/store/rootReducer";

interface Column<T> {
  name: string;
  width?: string;
  selector?: (row: T) => React.ReactNode;
  cell?: (row: T) => React.ReactNode;
}

const Users: React.FC = () => {
  const {
    debouncedSearch,
    search,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
  } = useSearchPagination();

  const dispatch = useDispatch<AppDispatch>();

  const { isFetching, users, totalUsers } = useSelector(
    (state: RootState) => state.user
  );
  const { modalOpen } = useSelector((state: RootState) => state.global);

  const actionsCellCallback = (row: User) => (
    <ActionsCell
      id={row.id}
      image={row.avatar || null} // or any placeholder
      onView={undefined}
      onText={undefined}
      onApprove={undefined}
      onReject={undefined}
      onEdit={
        checkPermission("update user") &&
        (() => {
          dispatch(setModalOpen({ id: MODAL_ID.ADD_USER, data: row }));
        })
      }
      onDelete={
        checkPermission("delete user") &&
        (() => {
          dispatch(
            setModalOpen({
              id: MODAL_ID.CONFIRM_DELETE,
              data: {
                id: row.id,
                userName: `"${row.firstName} ${row.lastName}"`,
              },
            })
          );
        })
      }
    />
  );

  const columns: Column<User>[] = [
    {
      name: "Name",
      width: "160px",
      selector: (row: User) => `${row.firstName} ${row.lastName}`,
    },
    {
      name: "User Name",
      width: "160px",
      selector: (row: User) => `${row.userName} `,
    },
    {
      name: "Email",
      width: "280px",
      selector: (row: User) => row.email,
      cell: (row: User) => (
        <Tooltip id={row?.email} data={row?.email || "abc"} maxLength={35} />
      ),
    },
    {
      name: "Mobile Number",
      width: "140px",
      selector: (row: User) => row.mobileNumber,
    },
    {
      name: "Status",
      width: "180px",
      selector: (row: User) => (
        <span className={row.isActive ? "status-active" : "status-inactive"}>
          {row.isActive ? "Active" : "In-Active"}
        </span>
      ),
    },
    {
      name: "Action",
      width: "100px",
      cell: actionsCellCallback,
    },
  ];

  const getData = useCallback(() => {
    dispatch(
      getUsers({
        search: debouncedSearch,
        page: pageSelected,
        limit: rowsPerPageValue,
      })
    );
  }, [dispatch, debouncedSearch, pageSelected, rowsPerPageValue]);

  const openAddUserModal = () => {
    dispatch(setModalOpen({ id: MODAL_ID.ADD_USER }));
  };

  const handleDelete = (id: number | string) => async () => {
    try {
      const data = await UserService.deleteUser(id); // now data has a message
      getData();
      toast.success(data?.message || "User deleted successfully");
      dispatch(setModalOpen({ id: null, data: null }));
    } catch (error) {
      console.log(`Exception while doing something: ${error}`);
    }
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <AddUser getData={getData} />
      <ConfirmDeleteModal
        handleDelete={handleDelete(modalOpen?.data?.id)}
        deleteLabel="user"
      />
      <div className={styles.main}>
        <CustomHeader
          search={search}
          setSearch={setSearch}
          onButtonClick={checkPermission("add user") && openAddUserModal}
          buttonLabel="Add"
        />
        <Table
          columns={columns}
          data={users}
          className={styles.candidatesTable}
          progressPending={isFetching}
          paginationProps={{
            isPagination: true,
            tableName: "Accounts",
            pageSelected,
            totalCount: totalUsers,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>
    </>
  );
};

export default Users;
